import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { ServicePicker } from "./ServicePicker";
import { DEFAULT_SERVICE } from "@/lib/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "mt-1 sm:mt-2 w-full bg-transparent border-b border-ink/40 py-1.5 sm:py-2 text-base normal-case tracking-normal font-normal outline-none focus:border-plate transition-colors";
const today = new Date(new Date().setHours(0, 0, 0, 0));
const EMPTY = { name: "", phone: "", email: "", services: [DEFAULT_SERVICE] };
const fmtDay = (d) => format(d, "EEEE, d. MMMM", { locale: de });
const endOf = (t, min) => {
  const [h, m] = t.split(":").map(Number);
  const e = h * 60 + m + min;
  return `${String(Math.floor(e / 60)).padStart(2, "0")}:${String(e % 60).padStart(2, "0")}`;
};

const BookingCtx = createContext(() => {});
export const useBooking = () => useContext(BookingCtx);

export const BookingProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState(null);
  const openBooking = (service) => {
    setPreset(typeof service === "string" ? service : null);
    setOpen(true);
  };
  return (
    <BookingCtx.Provider value={openBooking}>
      {children}
      <BookingDialog open={open} onOpenChange={setOpen} preset={preset} />
    </BookingCtx.Provider>
  );
};

const SlotGrid = ({ slots, time, setTime, minutes }) => (
  <div className="mt-3 sm:mt-4 grid grid-cols-4 gap-1.5 sm:gap-2" data-testid="booking-slots">
    {slots.map((s) => (
      <button key={s.time} type="button" disabled={!s.available} onClick={() => setTime(s.time)}
        title={`${s.time} – ${endOf(s.time, minutes)} Uhr`}
        className={`tn text-[13px] sm:text-sm py-1.5 sm:py-2 rounded-md sm:rounded-none border transition-colors disabled:opacity-30 disabled:line-through ${time === s.time ? "bg-plate text-white border-plate" : "border-ink/30 hover:border-plate"}`}
        data-testid={`booking-slot-${s.time.replace(":", "")}`}>{s.time}</button>
    ))}
  </div>
);

const BookingDialog = ({ open, onOpenChange, preset }) => {
  const [day, setDay] = useState();
  const [slots, setSlots] = useState([]);
  const [minutes, setMinutes] = useState(45);
  const [time, setTime] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const dateStr = day ? format(day, "yyyy-MM-dd") : "";

  useEffect(() => {
    if (!open || holidays.length) return;
    axios.get(`${API}/bookings/holidays`)
      .then((r) => setHolidays(r.data.holidays.map((h) => new Date(`${h.date}T00:00:00`))))
      .catch(() => {});
  }, [open, holidays.length]);

  useEffect(() => {
    if (open && preset) setForm((f) => ({ ...f, services: [preset] }));
  }, [open, preset]);

  useEffect(() => {
    setTime("");
    if (!dateStr) return setSlots([]);
    axios.get(`${API}/bookings/slots`, { params: { date: dateStr } })
      .then((r) => { setSlots(r.data.slots); setMinutes(r.data.minutes); })
      .catch(() => setSlots([]));
  }, [dateStr]);

  const submit = async (e) => {
    e.preventDefault();
    if (!day || !time) return toast.error("Bitte Tag und Uhrzeit wählen.");
    if (form.services.length === 0) return toast.error("Bitte mindestens eine Leistung wählen.");
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, date: dateStr, time });
      toast.success(`Termin am ${fmtDay(day)} um ${time} Uhr ist reserviert. Die Bestätigung kommt per E-Mail.`);
      onOpenChange(false);
      setDay(undefined);
      setForm(EMPTY);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Buchung fehlgeschlagen.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[94vw] sm:w-full rounded-2xl sm:rounded-none bg-paper text-ink max-w-4xl max-h-[88vh] sm:max-h-[92vh] overflow-y-auto p-4 sm:p-6 gap-3 sm:gap-4" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="dp text-2xl sm:text-4xl font-normal text-left">Termin online buchen</DialogTitle>
          <DialogDescription className="text-inkmuted text-xs sm:text-sm text-left">Tag wählen, Uhrzeit wählen, fertig. Jeder Termin dauert {minutes} Minuten.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid md:grid-cols-[auto_1fr] gap-4 sm:gap-8">
          <div>
            <Calendar mode="single" selected={day} onSelect={setDay} locale={de} weekStartsOn={1}
              disabled={[{ before: today }, { dayOfWeek: [0, 6] }, ...holidays]} className="pc-cal border border-ink/15 bg-white w-full sm:w-fit p-2 sm:p-3 rounded-xl sm:rounded-none" data-testid="booking-calendar" />
            <SlotGrid slots={slots} time={time} setTime={setTime} minutes={minutes} />
            {!day && <p className="mt-2 sm:mt-4 text-xs sm:text-sm text-inkmuted">Mo–Do 10–18 Uhr, Fr 10–13 Uhr. An Feiertagen geschlossen.</p>}
          </div>
          <div className="grid gap-3 sm:gap-4 content-start min-w-0">
            <label className="lb text-inkmuted">Name<input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="booking-name-input" /></label>
            <label className="lb text-inkmuted">Telefon<input required minLength={5} type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="booking-phone-input" /></label>
            <label className="lb text-inkmuted">E-Mail (für Ihre Terminbestätigung)<input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="booking-email-input" /></label>
            <div className="lb text-inkmuted">Leistungen (mehrere möglich)<ServicePicker value={form.services} onChange={(v) => setForm({ ...form, services: v })} testId="booking-service-picker" /></div>
            <p className="text-sm text-inkmuted" data-testid="booking-summary">{day && time ? `${fmtDay(day)} · ${time} – ${endOf(time, minutes)} Uhr` : "Noch kein Termin gewählt"}</p>
            <button type="submit" disabled={sending} className="lb bg-plate text-white px-6 py-3.5 sm:py-4 rounded-full sm:rounded-none transition-colors hover:bg-plate-hover disabled:opacity-60" data-testid="booking-submit-btn">
              {sending ? "Wird gebucht …" : "Termin verbindlich anfragen"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
